import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, FileUp, Upload } from 'lucide-react';
import { api, ApiError } from '../../api/cliente.js';
import { Botao } from '../../componentes/ui/Botao.js';
import { Modal } from '../../componentes/ui/Modal.js';

interface Problema {
  linha: number;
  conteudo: string;
  motivo: string;
}

interface Resultado {
  lidos: number;
  novos: number;
  substituicoes: number;
  inalterados: number;
  problemas: Problema[];
  foraDoBloco: Problema[];
  repetidos: string[];
  amostraSubstituicoes: { ip: string; de: string; para: string }[];
  amostraNovos: { ip: string; ptr: string }[];
  aplicado: boolean;
  gravados: number;
  diretosCriados: number;
}

interface ModalImportarProps {
  aberto: boolean;
  aoFechar: () => void;
  blocoId: string;
  cidr: string;
  aoImportar: () => void;
}

const EXEMPLO = `200.150.10.1,host-1.cliente.com.br
200.150.10.2;host-2.cliente.com.br
2.10.150.200.in-addr.arpa. IN PTR host-2.cliente.com.br.`;

/**
 * Importação de PTRs a partir de arquivo.
 *
 * O fluxo é sempre em dois tempos: analisar, depois confirmar. A prévia vem do
 * servidor, da mesma rota e do mesmo código que grava — uma análise no
 * navegador divergiria em algum caso de borda e o operador confirmaria uma
 * coisa enquanto outra seria gravada.
 *
 * O que a tela destaca é a **substituição**, não o total. Importar 800
 * registros novos é inofensivo; sobrescrever 12 que já existiam é o que
 * ninguém quer descobrir depois.
 */
export function ModalImportar({
  aberto,
  aoFechar,
  blocoId,
  cidr,
  aoImportar,
}: ModalImportarProps) {
  const [texto, setTexto] = useState('');
  const [previa, setPrevia] = useState<Resultado | null>(null);
  const arquivoRef = useRef<HTMLInputElement>(null);

  function limpar() {
    setTexto('');
    setPrevia(null);
    analisar.reset();
    aplicar.reset();
  }

  function fechar() {
    limpar();
    aoFechar();
  }

  // O arquivo é lido no navegador e enviado como texto. Não há upload: o
  // conteúdo é pequeno, e um endpoint multipart traria um caminho de entrada a
  // mais para proteger sem ganhar nada.
  async function carregarArquivo(arquivo: File | undefined) {
    if (!arquivo) return;
    setPrevia(null);
    setTexto(await arquivo.text());
  }

  const analisar = useMutation({
    mutationFn: () =>
      api.post<Resultado>(`/blocos/${blocoId}/ptr/importar`, { texto, aplicar: false }),
    onSuccess: setPrevia,
  });

  const aplicar = useMutation({
    mutationFn: () =>
      api.post<Resultado>(`/blocos/${blocoId}/ptr/importar`, { texto, aplicar: true }),
    onSuccess: (r) => {
      aoImportar();
      setPrevia(r);
    },
  });

  const gravavel = previa ? previa.novos + previa.substituicoes : 0;
  const concluido = aplicar.isSuccess;

  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      largura="largo"
      titulo="Importar reversos de arquivo"
      descricao={`Os endereços fora de ${cidr} são ignorados e listados no relatório.`}
      rodape={
        concluido ? (
          <Botao variante="primario" onClick={fechar}>
            Fechar
          </Botao>
        ) : (
          <>
            <Botao onClick={fechar} disabled={analisar.isPending || aplicar.isPending}>
              Cancelar
            </Botao>
            {previa ? (
              <Botao
                variante="primario"
                onClick={() => aplicar.mutate()}
                carregando={aplicar.isPending}
                disabled={gravavel === 0}
              >
                {previa.substituicoes > 0
                  ? `Importar e sobrescrever ${previa.substituicoes}`
                  : `Importar ${gravavel}`}
              </Botao>
            ) : (
              <Botao
                variante="primario"
                onClick={() => analisar.mutate()}
                carregando={analisar.isPending}
                disabled={texto.trim().length === 0}
              >
                Analisar
              </Botao>
            )}
          </>
        )
      }
    >
      {!concluido && (
        <>
          <div className="mb-2.5 flex items-center justify-between gap-3">
            <span className="text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
              Conteúdo do arquivo
            </span>
            <button
              type="button"
              onClick={() => arquivoRef.current?.click()}
              className="flex items-center gap-1.5 rounded-chip border border-borda bg-fundo px-2.5 py-1 text-[11.5px] font-semibold text-texto-medio hover:border-marca hover:text-marca"
            >
              <FileUp size={13} />
              Escolher arquivo
            </button>
            <input
              ref={arquivoRef}
              type="file"
              accept=".csv,.txt,.zone,text/plain"
              className="hidden"
              onChange={(e) => void carregarArquivo(e.target.files?.[0])}
            />
          </div>

          <textarea
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              setPrevia(null);
            }}
            rows={8}
            spellCheck={false}
            placeholder={EXEMPLO}
            className="w-full rounded-controle border border-borda px-3 py-2 font-mono text-[12.5px] leading-relaxed focus:border-marca focus:outline-none"
          />

          <p className="mt-2 text-[11.5px] leading-relaxed text-texto-suave">
            Aceita vírgula, ponto e vírgula, tabulação ou espaço como separador, e também o
            formato de arquivo de zona (a saída de um <span className="font-mono">AXFR</span> do
            provedor atual). Linhas em branco e comentários são ignorados.
          </p>
        </>
      )}

      {(analisar.error || aplicar.error) && (
        <p className="mt-4 flex items-start gap-2 rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
          <AlertTriangle size={15} className="mt-px shrink-0" />
          {((analisar.error ?? aplicar.error) as ApiError).descricao}
        </p>
      )}

      {previa && (
        <div className="mt-5 space-y-4">
          {concluido && (
            <p className="flex items-start gap-2 rounded-chip bg-sucesso-fundo px-3 py-2 text-[12.5px] font-semibold text-sucesso">
              <Upload size={15} className="mt-px shrink-0" />
              {previa.gravados} {previa.gravados === 1 ? 'reverso publicado' : 'reversos publicados'}
              {previa.diretosCriados > 0 && `, ${previa.diretosCriados} com registro direto`}.
            </p>
          )}

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Contagem rotulo="Lidos" valor={previa.lidos} />
            <Contagem rotulo="Novos" valor={previa.novos} />
            <Contagem rotulo="Sobrescreve" valor={previa.substituicoes} alerta={previa.substituicoes > 0} />
            <Contagem rotulo="Já iguais" valor={previa.inalterados} />
          </div>

          {/* Primeiro na tela por ser o único efeito destrutivo da operação. */}
          {previa.amostraSubstituicoes.length > 0 && (
            <Secao
              titulo={`Reversos que serão substituídos${
                previa.substituicoes > previa.amostraSubstituicoes.length
                  ? ` (${previa.amostraSubstituicoes.length} de ${previa.substituicoes})`
                  : ''
              }`}
              alerta
            >
              {previa.amostraSubstituicoes.map((s) => (
                <tr key={s.ip} className="border-b border-borda-suave last:border-0">
                  <td className="w-[30%] px-3 py-2 font-mono text-texto-suave">{s.ip}</td>
                  <td className="px-3 py-2 font-mono text-[12px]">
                    <span className="text-erro line-through">{s.de}</span>
                    <span className="mx-1.5 text-texto-suave">→</span>
                    <span>{s.para}</span>
                  </td>
                </tr>
              ))}
            </Secao>
          )}

          {previa.foraDoBloco.length > 0 && (
            <Secao titulo={`Fora do bloco, ignorados (${previa.foraDoBloco.length})`}>
              {previa.foraDoBloco.slice(0, 10).map((p) => (
                <tr key={`${p.linha}-${p.conteudo}`} className="border-b border-borda-suave last:border-0">
                  <td className="w-[15%] px-3 py-2 text-texto-suave">linha {p.linha}</td>
                  <td className="px-3 py-2 font-mono text-[12px]">{p.conteudo}</td>
                </tr>
              ))}
            </Secao>
          )}

          {previa.problemas.length > 0 && (
            <Secao titulo={`Linhas com defeito, ignoradas (${previa.problemas.length})`} alerta>
              {previa.problemas.slice(0, 10).map((p) => (
                <tr key={`${p.linha}-${p.conteudo}`} className="border-b border-borda-suave last:border-0">
                  <td className="w-[15%] px-3 py-2 text-texto-suave">linha {p.linha}</td>
                  <td className="px-3 py-2">
                    <span className="font-mono text-[12px]">{p.conteudo}</span>
                    <span className="ml-2 text-[11.5px] font-semibold text-erro">{p.motivo}</span>
                  </td>
                </tr>
              ))}
            </Secao>
          )}

          {previa.repetidos.length > 0 && (
            <p className="text-[11.5px] leading-relaxed text-texto-suave">
              {previa.repetidos.length}{' '}
              {previa.repetidos.length === 1 ? 'endereço aparece' : 'endereços aparecem'} mais de
              uma vez no arquivo. Vale a última ocorrência de cada um.
            </p>
          )}

          {!concluido && gravavel === 0 && (
            <p className="rounded-chip bg-fundo px-3 py-2 text-[12.5px] text-texto-medio">
              Nada a gravar: o arquivo não trouxe nenhum reverso novo ou diferente do atual.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

function Contagem({
  rotulo,
  valor,
  alerta,
}: {
  rotulo: string;
  valor: number;
  alerta?: boolean;
}) {
  return (
    <div
      className={`rounded-chip border px-3 py-2 ${
        alerta ? 'border-erro/30 bg-erro-fundo' : 'border-borda bg-fundo'
      }`}
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">{rotulo}</p>
      <p className={`text-[18px] font-bold ${alerta ? 'text-erro' : 'text-texto'}`}>{valor}</p>
    </div>
  );
}

function Secao({
  titulo,
  alerta,
  children,
}: {
  titulo: string;
  alerta?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p
        className={`mb-2 text-[11px] font-bold uppercase tracking-[0.6px] ${
          alerta ? 'text-erro' : 'text-texto-suave'
        }`}
      >
        {titulo}
      </p>
      <div className="max-h-64 overflow-auto rounded-chip border border-borda">
        <table className="w-full border-collapse text-[12.5px]">
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}
